import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, Loader, EmptyState, Pagination, PageHeader, Modal, Button } from '../../../components/ui';
import { emptyStateIcon } from '../../../assets/Icons/icons';
import { useAppMutation, useAppQuery } from '../../../api/fetchClient';
import { QUERY_KEYS } from '../../../api/queryKeys';
import { toast } from 'react-toastify';
import { GET_REQUESTS, UPDATE_REQUEST_STATUS } from '../../../api/url';
import RequestToolbar   from './components/RequestToolbar';
import { ListRequestCard, GridRequestCard } from './components/RequestCards';
import { strings } from '../../../strings/strings';

const Request = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm,  setSearchTerm]  = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy,      setSortBy]      = useState('all');
  const [viewMode,    setViewMode]    = useState('three');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 1000);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [rejectReason, setRejectReason] = useState('');

  const {
    data: requestsData,
    isLoading,
    isError,
    error,
    // eslint-disable-next-line no-unused-vars
    refetch: refetchRequests,
  } = useAppQuery({
    queryKey: [QUERY_KEYS.REQUESTS, currentPage, debouncedSearchTerm, statusFilter, sortBy],
    url: `${GET_REQUESTS}?page=${currentPage}&limit=12${debouncedSearchTerm ? `&search=${debouncedSearchTerm}` : ''}${statusFilter !== 'all' ? `&status=${statusFilter}` : ''}${sortBy !== 'all' ? `&sort=${sortBy}` : ''}`,
    method: 'GET',
    options: { staleTime: 0 },
  });

  const { mutate, isPending: isMutating } = useAppMutation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (requestsData) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REQUESTS, "count"] });
    }
  }, [requestsData, queryClient]);


  const requestsList = Array.isArray(requestsData?.results?.data)
    ? requestsData.results.data
    : Array.isArray(requestsData?.data)
    ? requestsData.data
    : [];

  const pagination    = requestsData?.results?.pagination || null;
  const totalResults  = requestsData?.results?.totalItems ;

  const gridClass =
    viewMode === 'one'
      ? 'grid grid-cols-1 gap-6'
      : viewMode === 'two'
      ? 'grid grid-cols-1 md:grid-cols-2 gap-6'
      : 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6';

const formatDate = (dateString) =>
  dateString
    ? new Date(dateString).toLocaleString("en-GB", {
      timeZone: "UTC",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "N/A";
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleSearch     = (e)       => { setSearchTerm(e.target.value); };
  const handleSort = (sort) => {
    if (sort === 'all') {
      setStatusFilter('all');
      setSortBy('all');
    } else {
      setSortBy(sort);
      if (statusFilter !== 'all') setStatusFilter('all');
    }
    setCurrentPage(1);
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setIsApproveModalOpen(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setIsRejectModalOpen(true);
    setRejectReason('');
  };

  const handleStatusUpdate = (status) => {
    if (!selectedRequest) return;

    const payload = {
      hcp_id: selectedRequest.id,
      status: status,
    };

    if (status === 'rejected') {
      /*
      if (!rejectReason.trim()) {
        toast.error('Please provide a reason for rejection');
        return;
      }
      payload.reason = rejectReason;
      */
    }

    mutate(
      { url: UPDATE_REQUEST_STATUS, method: 'POST', data: payload },
      {
        onSuccess: (res) => {
          toast.success(res.message || `Request ${status} successfully`);
          setIsApproveModalOpen(false);
          setIsRejectModalOpen(false);
          setSelectedRequest(null);
          if (status === 'rejected') setRejectReason('');
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REQUESTS] });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.HCP] });
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || `Failed to ${status} request`);
        },
      }
    );
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto">
        <PageHeader title={strings.requestTitle} description={strings.requestsDescription} className="mb-8" />

        <RequestToolbar
          searchTerm={searchTerm}
          onSearch={handleSearch}
          sortBy={sortBy}
          onSort={handleSort}
          viewMode={viewMode}
          onViewChange={setViewMode}
          totalResults={totalResults}
        />

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader />
          </div>
        ) : isError ? (
          <Card className="shadow-sm border border-gray-200">
            <div className="py-12">
              <EmptyState icon={emptyStateIcon} description={error?.message} />
            </div>
          </Card>
        ) : requestsList.length === 0 ? (
          <Card className="shadow-sm border border-gray-200">
            <div className="py-12">
              <EmptyState
                icon={emptyStateIcon}
                title={strings.noPendingRequests}
                description={strings.allHealthcareProviderRequestsProcessed}
              />
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            <div className={gridClass}>
              {requestsList.map((request) => (
                <React.Fragment key={request.id}>

                  {viewMode === "one" && (
                    <div className="hidden lg:block">
                      <Card className="bg-white shadow-sm">
                        <ListRequestCard
                          request={request}
                          isMutating={isMutating}
                          onApprove={handleApprove}
                          onReject={handleReject}
                          formatDate={formatDate}
                        />
                      </Card>
                    </div>
                  )}

                  <div className={viewMode === "one" ? "lg:hidden" : "block"}>
                    <GridRequestCard
                      request={request}
                      isMutating={isMutating}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      formatDate={formatDate}
                    />
                  </div>
                </React.Fragment>
              ))}
            </div>

            {pagination && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <Pagination
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  currentItemsCount={requestsList.length}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        title="Confirm Approval"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setIsApproveModalOpen(false)} disabled={isMutating}>
              {strings.cancel}
            </Button>
            <Button
              variant="primary"
              onClick={() => handleStatusUpdate("approved")}
              isLoading={isMutating}
              disabled={isMutating}
            >
{strings.approve}            </Button>
          </div>
        }
      >
        <p className="text-secondary text-sm">
          {strings.confirmApproval} {" "}
          <span className="font-bold text-secondary">
            {selectedRequest?.first_name} {selectedRequest?.last_name}
          </span>
          ?
        </p>
      </Modal>


      {/*
      <Modal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        title="Reject Request"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setIsRejectModalOpen(false)} disabled={isMutating}>
              {strings.cancel}
            </Button>
            <Button
              variant="danger"
              onClick={() => handleStatusUpdate("rejected")}
              isLoading={isMutating}
              disabled={isMutating}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {strings.reject}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-secondary text-sm">
            {strings.confirmRejection} {" "}
            <span className="font-bold text-secondary">
              {selectedRequest?.first_name} {selectedRequest?.last_name}
            </span>
            .
          </p>
          <Textarea
            label={strings.rejectionReason}
            placeholder={strings.enterRejectionReason}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            required
            rows={4}
          />
        </div>
      </Modal>
      */}

      <Modal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        title="Confirm Reject"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setIsRejectModalOpen(false)} disabled={isMutating}>
              {strings.cancel}
            </Button>
            <Button
              variant="danger"
              onClick={() => handleStatusUpdate("rejected")}
              isLoading={isMutating}
              disabled={isMutating}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {strings.reject}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-secondary text-sm">
            {strings.confirmRejection} {" "}
            <span className="font-bold text-secondary">
              {selectedRequest?.first_name} {selectedRequest?.last_name}
            </span>
            ?
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Request;